import { WidgetParams } from '../types';

/**
 * Parses URL search parameters to extract widget configuration
 * @returns WidgetParams object with environment, vendorId, and optional theme/color parameters
 */
export const parseUrlParams = (): WidgetParams => {
  const urlParams = new URLSearchParams(window.location.search);
  
  const environment = urlParams.get('environment') || 'app';
  const vendorId = urlParams.get('vendorId');
  const primaryColor = urlParams.get('primaryColor') || undefined;
  const themeParam = urlParams.get('theme');
  const theme = (themeParam === 'light' || themeParam === 'dark') ? themeParam : undefined;

  return {
    environment,
    vendorId,
    primaryColor,
    theme
  };
};

/**
 * Validates that required parameters are present
 * @param params WidgetParams object to validate
 * @returns true if valid, false otherwise
 */
export const validateParams = (params: WidgetParams): boolean => {
  return !!(params.vendorId);
};

/**
 * Generates the next 7 days starting from today
 * @returns Array of date strings in YYYY-MM-DD format
 */
export const getNext7Days = (): string[] => {
  const dates: string[] = [];
  const today = new Date();
  
  for (let i = 0; i < 7; i++) {
    const date = new Date(today);
    date.setDate(today.getDate() + i);
    dates.push(date.toISOString().split('T')[0]);
  }
  
  return dates;
};

/**
 * Formats a date string to display day of week
 * @param dateString Date in YYYY-MM-DD format
 * @returns Formatted day name (e.g., "Monday", "Tuesday")
 */
export const formatDayOfWeek = (dateString: string): string => {
  try {
    if (!dateString) return 'Unknown';
    
    // Ensure consistent date parsing by adding time component
    const date = new Date(dateString + 'T00:00:00');
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.warn('Invalid date string for formatDayOfWeek:', dateString);
      return 'Unknown';
    }
    
    return date.toLocaleDateString('en-US', { weekday: 'long' });
  } catch (error) {
    console.error('Error formatting day of week:', error, dateString);
    return 'Unknown';
  }
};

/**
 * Formats a date string to display readable date
 * @param dateString Date in YYYY-MM-DD format
 * @returns Formatted date (e.g., "Jan 15", "Dec 31")
 */
export const formatDate = (dateString: string): string => {
  try {
    if (!dateString) return 'Unknown';
    
    // Ensure consistent date parsing by adding time component
    const date = new Date(dateString + 'T00:00:00');
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.warn('Invalid date string for formatDate:', dateString);
      return 'Unknown';
    }
    
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch (error) {
    console.error('Error formatting date:', error, dateString);
    return 'Unknown';
  }
};

/**
 * Converts 24-hour time format to 12-hour AM/PM format
 * @param time24 Time in 24-hour format (e.g., "14:30", "09:00")
 * @returns Time in 12-hour format with AM/PM (e.g., "2:30 PM", "9:00 AM")
 */
export const formatTime12Hour = (time24: string | null): string | null => {
  if (!time24) {
    console.log('formatTime12Hour received null/undefined:', time24);
    return null;
  }
  
  try {
    // Handle different time formats
    let timeStr = time24.toString().trim();
    
    console.log('formatTime12Hour processing:', timeStr);
    
    // If it already has AM/PM, return as is
    if (timeStr.includes('AM') || timeStr.includes('PM')) {
      return timeStr;
    }
    
    // Extract hours and minutes from various formats
    const timeMatch = timeStr.match(/(\d{1,2}):(\d{2})/);
    if (!timeMatch) {
      console.warn('Could not parse time format:', time24);
      return 'Invalid Time'; // Return a clear error message instead of original
    }
    
    const hours = parseInt(timeMatch[1], 10);
    const minutes = parseInt(timeMatch[2], 10);
    
    if (isNaN(hours) || isNaN(minutes) || hours > 23 || minutes > 59) {
      console.warn('Invalid time values:', { hours, minutes, original: time24 });
      return 'Invalid Time'; // Return a clear error message instead of original
    }
    
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    
    const result = `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
    console.log('formatTime12Hour result:', result);
    return result;
  } catch (error) {
    console.error('Error formatting time:', error, time24);
    return 'Invalid Time';
  }
};

/**
 * Validates a hex color string (without #)
 * @param color Hex color string without # (e.g., "667eea")
 * @returns true if valid hex color, false otherwise
 */
export const isValidHexColor = (color: string): boolean => {
  if (!color || typeof color !== 'string') {
    return false;
  }
  
  // Check if it's a valid 3 or 6 character hex string
  const hexRegex = /^[0-9A-Fa-f]{3}$|^[0-9A-Fa-f]{6}$/;
  return hexRegex.test(color);
};

/**
 * Generates theme-based styling for the widget
 * @param primaryColor Hex color without # (optional)
 * @param theme Light or dark theme (optional)
 * @returns Object with CSS custom properties for theming
 */
export const generateThemeStyles = (primaryColor?: string, theme?: 'light' | 'dark'): Record<string, string> => {
  // Default values
  const defaultPrimaryColor = '667eea';
  const defaultTheme = 'dark';
  
  // Validate and use provided values or fall back to defaults
  const validPrimaryColor = primaryColor && isValidHexColor(primaryColor) ? primaryColor : defaultPrimaryColor;
  const validTheme = theme || defaultTheme;
  
  const styles: Record<string, string> = {
    '--primary-color': `#${validPrimaryColor}`,
    '--theme': validTheme
  };

  if (validTheme === 'light') {
    styles['--bg-color'] = '#ffffff';
    styles['--text-color'] = '#333333';
    styles['--secondary-text-color'] = '#666666';
    styles['--border-color'] = '#e0e0e0';
    styles['--card-bg'] = '#f8f9fa';
    styles['--shadow-color'] = 'rgba(0, 0, 0, 0.1)';
    styles['--shadow-hover'] = 'rgba(0, 0, 0, 0.15)';
    styles['--success-color'] = '#22c55e';
    styles['--success-bg'] = 'rgba(34, 197, 94, 0.1)';
    styles['--error-color'] = '#ef4444';
    styles['--error-bg'] = 'rgba(239, 68, 68, 0.1)';
  } else {
    styles['--bg-color'] = '#1a1a1a';
    styles['--text-color'] = '#ffffff';
    styles['--secondary-text-color'] = '#cccccc';
    styles['--border-color'] = '#333333';
    styles['--card-bg'] = '#2a2a2a';
    styles['--shadow-color'] = 'rgba(0, 0, 0, 0.3)';
    styles['--shadow-hover'] = 'rgba(0, 0, 0, 0.2)';
    styles['--success-color'] = '#22c55e';
    styles['--success-bg'] = 'rgba(34, 197, 94, 0.2)';
    styles['--error-color'] = '#ff6b6b';
    styles['--error-bg'] = 'rgba(255, 107, 107, 0.2)';
  }

  return styles;
};
